import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { InstantTime } from '../../lib/time-format';
import { useZoneReadReport } from '../../components/ZoneContext';
import { ZoneFilter } from '../../components/ZoneIndicators';
import { ZONE_TABLE, consoleZoneLabel, HOME_ZONE } from '../../lib/zones';
import { listFollowUp, type FollowUpRow } from '../../lib/follow-up-api';
import { ZoneCell } from '../../components/ZoneIndicators';

/**
 * /admin/launchpad-follow-up — ADR-127 D17 (ADR-073 Amendment 6 §A6.3).
 *
 * Jeff, 11 October 2026 (the revised accountability rule): customers can switch guidance off, and
 * once off they see only honest status in place — so Nexanomics stays accountable through people.
 * This is that list: every paying customer whose snippet is still not live about a week after
 * sign-up, in every zone, with a per-zone legend that is always visible (the Alerts board's rule:
 * holistic, zone-agnostic, filterable, a legend for each zone). Read-only — outreach happens by
 * email or phone, outside the console.
 */
export function LaunchpadFollowUpPage() {
  const toast = useToast();
  const [rows, setRows] = useState<FollowUpRow[] | null>(null);
  const [afterDays, setAfterDays] = useState(7);
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState('all');
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const r = await listFollowUp();
      setRows(r.rows);
      setAfterDays(r.afterDays);
      setFailedZones(r.failedZones);
    } catch (e) {
      setError(true);
      setRows([]);
      toast.push({ variant: 'error', ...toastFromError(e, 'Couldn’t load the follow-up list') });
    }
  }, [toast]);
  useEffect(() => { void load(); }, [load]);

  useZoneReadReport({ rows, failedZones, filter: zoneFilter });
  const legend = useMemo(() => ZONE_TABLE.map((z) => ({
    code: z.code, label: consoleZoneLabel(z.code), n: (rows ?? []).filter((r) => (r.zone ?? HOME_ZONE.code) === z.code).length,
  })), [rows]);
  const shown = (rows ?? []).filter((r) => zoneFilter === 'all' || (r.zone ?? HOME_ZONE.code) === zoneFilter);

  return (
    <div className="alerts-page" data-testid="follow-up-page">
      <h1>Snippet follow-up</h1>
      <div className="alerts-crumb">
        Admin → COG → Snippet follow-up &nbsp;·&nbsp; paying customers whose snippet is still not live {afterDays}+ days after sign-up — call or email them
      </div>
      {failedZones.length > 0 && (
        <div className="error-banner" role="alert">{failedZones.join(', ')} did not respond — its customers are missing from this list.</div>
      )}
      <table className="alerts-zone-legend" data-testid="follow-up-zone-legend" aria-label="Follow-up by zone">
        <thead><tr><th scope="col">Zone</th><th scope="col">Not live yet</th></tr></thead>
        <tbody>
          {legend.map((r) => (
            <tr key={r.code} data-zone-row={r.code}><th scope="row"><ZoneCell zone={r.code} /></th><td>{rows ? r.n : '…'}</td></tr>
          ))}
        </tbody>
      </table>
      <div className="alerts-filters">
        <div className="alerts-controls">
          <ZoneFilter value={zoneFilter} onChange={setZoneFilter} className="alerts-zone-filter" />
          <button type="button" className="alerts-fpill" onClick={() => void load()}>Refresh</button>
        </div>
      </div>
      {rows === null ? (
        <div className="follow-up-loading" role="status" aria-live="polite">
          <div className="skeleton-row" aria-hidden="true" /><div className="skeleton-row" aria-hidden="true" />
          <p className="muted">Loading the follow-up list…</p>
        </div>
      ) : shown.length === 0 ? (
        <p className="muted" data-testid="follow-up-empty">
          {error ? 'The list could not be loaded — try Refresh.' : `Nobody to follow up — every paying customer's snippet went live within ${afterDays} days.`}
        </p>
      ) : (
        <table className="alerts-table" data-testid="follow-up-table">
          <thead>
            <tr>
              <th scope="col">Domain</th><th scope="col">Business</th><th scope="col">Owner</th>
              <th scope="col">Signed up</th><th scope="col">Days</th><th scope="col">Snippet email</th>
              <th scope="col">Ever fired</th><th scope="col">Zone</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={`${r.zone}|${r.domainToken}`}>
                <td>{r.hostname || r.domainToken}</td>
                <td>{r.businessName}</td>
                <td>{r.ownerName}{r.ownerEmail ? <><br /><a href={`mailto:${r.ownerEmail}`}>{r.ownerEmail}</a></> : null}</td>
                <td><InstantTime iso={r.signedUpAt} /></td>
                <td>{r.daysSinceSignUp}</td>
                <td>{r.snippetEmailSent ? 'Sent' : 'Not sent'}</td>
                <td>{r.everFired ? 'Yes — no real visit yet' : 'No'}</td>
                <td><ZoneCell zone={r.zone ?? HOME_ZONE.code} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
